// src/services/media-upload-service.ts

import axios from "axios"
import { env } from "@/env"

// Regex to match /wp-json suffix (with optional trailing slash)
const WP_JSON_SUFFIX_REGEX = /\/wp-json\/?$/

// Use client-safe environment variable and extract WordPress origin
// NEXT_PUBLIC_WP_API_URL is typically "https://domain.com/wp-json"
// We need just "https://domain.com" for AJAX endpoints
const getWordPressOrigin = (): string => {
  const url = env.NEXT_PUBLIC_WP_API_URL
  // Remove /wp-json suffix if present
  return url.replace(WP_JSON_SUFFIX_REGEX, "")
}

export interface MediaAttachment {
  id: number
  key: string
  nonce: string
  url: string
  type: "image" | "video"
}

export interface UploadError {
  code:
    | "FILE_TOO_LARGE"
    | "INVALID_TYPE"
    | "UPLOAD_FAILED"
    | "NETWORK_ERROR"
    | "NONCE_FETCH_FAILED"
  message: string
}

const IMAGE_MAX_SIZE = 5 * 1024 * 1024 // 5MB
const VIDEO_MAX_SIZE = 50 * 1024 * 1024 // 50MB

export const ALLOWED_IMAGE_TYPES = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/gif",
]
export const ALLOWED_VIDEO_TYPES = [
  "video/mp4",
  "video/mpeg",
  "video/ogg",
  "video/webm",
  "video/quicktime",
  "video/x-msvideo",
]

export const MAX_MEDIA_COUNT = 2

export async function fetchUploadNonce(
  productId: number,
  sessionCookie: string
): Promise<string> {
  if (!sessionCookie || sessionCookie.trim() === "") {
    throw new Error("Authentication required for media upload")
  }

  try {
    const response = await axios.get<{
      ok: boolean
      nonce?: string
      message?: string
    }>("/api/media/upload-nonce", {
      params: { productId },
    })

    if (!(response.data.ok && response.data.nonce)) {
      throw new Error(
        response.data.message || "Invalid nonce response from server"
      )
    }

    return response.data.nonce
  } catch (error) {
    console.error("[Media Upload] Nonce fetch error:", error)
    if (error instanceof Error && error.message !== "Network Error") {
      throw error
    }
    throw new Error(
      "Failed to fetch upload nonce. Please check your connection."
    )
  }
}

export function validateMediaFile(
  file: File
): { valid: true } | { valid: false; error: UploadError } {
  const isImage = ALLOWED_IMAGE_TYPES.includes(file.type)
  const isVideo = ALLOWED_VIDEO_TYPES.includes(file.type)

  if (!(isImage || isVideo)) {
    return {
      valid: false,
      error: {
        code: "INVALID_TYPE",
        message:
          "Only images (PNG, JPG, GIF) and videos (MP4, MOV, etc.) are allowed",
      },
    }
  }

  if (isImage && file.size > IMAGE_MAX_SIZE) {
    return {
      valid: false,
      error: {
        code: "FILE_TOO_LARGE",
        message: "Image must be under 5MB",
      },
    }
  }

  if (isVideo && file.size > VIDEO_MAX_SIZE) {
    return {
      valid: false,
      error: {
        code: "FILE_TOO_LARGE",
        message: "Video must be under 50MB",
      },
    }
  }

  return { valid: true }
}

export async function uploadMediaToWordPress(
  file: File,
  productId: number,
  nonce: string,
  _sessionCookie: string
): Promise<MediaAttachment> {
  const formData = new FormData()
  formData.append("file", file)
  formData.append("productId", String(productId))
  formData.append("nonce", nonce)

  try {
    const response = await axios.post<{
      ok: boolean
      attachment?: {
        id: number
        key: string
        nonce: string
      }
      message?: string
    }>("/api/media/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })

    if (!(response.data.ok && response.data.attachment)) {
      throw new Error(response.data.message || "Upload failed")
    }

    const { id, key, nonce: deleteNonce } = response.data.attachment
    const isVideo = ALLOWED_VIDEO_TYPES.includes(file.type)

    return {
      id,
      key,
      nonce: deleteNonce,
      url: `${getWordPressOrigin()}/wp-content/uploads/${file.name}`,
      type: isVideo ? "video" : "image",
    }
  } catch (error) {
    // If it's a WordPress error message (from response.data.message), preserve it
    if (
      error instanceof Error &&
      error.message &&
      error.message !== "Network error"
    ) {
      throw error
    }
    // Otherwise wrap generic network errors
    throw new Error("Upload failed. Please try again.")
  }
}
